import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import { BarChart3, Eye, Heart, MessageCircle, Users, FileText, TrendingUp } from 'lucide-react';
import { API, authFetch } from '../lib/api';

export const AnalyticsPage = () => {
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      login();
      return;
    }
    if (user) fetchAnalytics();
  }, [authLoading, user]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/analytics`);
      if (response.ok) {
        setAnalytics(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
          </div>
        </main>
      </div>
    );
  }

  if (!analytics) return null;

  const stats = [
    { label: 'Total Views', value: analytics.total_views, icon: Eye, color: 'text-blue-500' },
    { label: 'Total Claps', value: analytics.total_claps, icon: Heart, color: 'text-rose-500' },
    { label: 'Comments', value: analytics.total_comments, icon: MessageCircle, color: 'text-amber-500' },
    { label: 'Followers', value: analytics.total_followers, icon: Users, color: 'text-green-500' },
    { label: 'Unique Readers', value: analytics.total_readers, icon: TrendingUp, color: 'text-purple-500' },
    { label: 'Published', value: analytics.published_count, icon: FileText, color: 'text-accent' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <BarChart3 className="w-6 h-6 text-accent" />
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>
            Analytics Dashboard
          </h1>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10" data-testid="analytics-stats">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-card border border-border rounded-lg p-4 text-center">
              <stat.icon className={`w-5 h-5 mx-auto mb-2 ${stat.color}`} />
              <p className="text-2xl font-bold">{stat.value.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Articles Table */}
        <h2 className="text-lg font-semibold mb-4">Article Performance</h2>
        <div className="border border-border rounded-lg overflow-hidden" data-testid="analytics-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-left">
                  <th className="px-4 py-3 font-medium">Article</th>
                  <th className="px-4 py-3 font-medium text-right">Views</th>
                  <th className="px-4 py-3 font-medium text-right">Claps</th>
                  <th className="px-4 py-3 font-medium text-right">Comments</th>
                  <th className="px-4 py-3 font-medium text-right">Read Time</th>
                  <th className="px-4 py-3 font-medium text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {analytics.articles.map(article => (
                  <tr key={article.article_id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/article/${article.article_id}`}
                        className="font-medium hover:text-accent transition-colors line-clamp-1"
                        data-testid={`analytics-article-${article.article_id}`}
                      >
                        {article.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{article.views_count}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{article.claps_count}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{article.comments_count}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{article.reading_time} min</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        article.is_published
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                      }`}>
                        {article.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {analytics.articles.length === 0 && (
          <div className="py-16 text-center">
            <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No articles yet. Start writing to see analytics!</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AnalyticsPage;
